import React from 'react';
import { TrendingDown, Clock, TimerOff } from 'lucide-react';
import { useCountdown } from '../../hooks/useCountdown';
import { formatPrice, getReductionProgress } from '../../utils/formatters';
import { useLanguage } from '../../context/LanguageContext';
import SlotNumber from '../ui/SlotNumber';

export default function PriceCountdown({ listing, compact = false }) {
  const {
    currentPrice, floorPrice, startPrice, dropAmount, nextDropAt,
    dropIntervalHours, dropInterval,
    isReserved, reserved,
  } = listing;
  const { t } = useLanguage();
  const intervalHours = dropIntervalHours ?? dropInterval ?? 8;
  const isRes         = isReserved ?? reserved ?? false;
  const { remaining, formatted, triggered } = useCountdown(nextDropAt);
  const progress  = getReductionProgress(startPrice, currentPrice, floorPrice);
  const atFloor   = currentPrice <= floorPrice;
  const expiresAt = listing.expiresAt;
  const expired   = expiresAt
    ? new Date(expiresAt).getTime() <= Date.now()
    : false;

  /* ── compact (ListingCard) ─────────────────────────────────────── */
  if (compact) {
    return (
      <div className="mt-1">
        <div className="flex items-center justify-between">
          <SlotNumber
            value={formatPrice(currentPrice)}
            heightEm={1.25}
            className={`text-lg font-semibold leading-none ${triggered ? 'text-apple-red' : 'text-apple-gray-dark'}`}
          />
          {isRes && (
            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-medium">
              {t('price_countdown.reserved')}
            </span>
          )}
        </div>

        {expired && (
          <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
            <TimerOff className="w-3 h-3" />
            <span>{t('price_countdown.expired_short')}</span>
          </div>
        )}
        {!expired && !atFloor && (
          <div className="flex items-center gap-1 text-xs text-apple-gray-mid mt-0.5">
            <TrendingDown className="w-3 h-3 text-apple-red" />
            <span>{formatPrice(dropAmount)} {t('price_countdown.drop_label')} · </span>
            <Clock className="w-3 h-3" />
            <SlotNumber value={formatted} heightEm={1.1} className="font-mono text-xs" />
          </div>
        )}
        {!expired && atFloor && (
          <span className="text-xs text-apple-green font-medium">
            {t('price_countdown.floor_reached_short')}
          </span>
        )}
      </div>
    );
  }

  /* ── full (ListingDetail) ──────────────────────────────────────── */
  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-4 border border-gray-100">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <TrendingDown className="w-4 h-4 text-apple-red" />
        <span className="text-xs font-semibold text-apple-gray-mid uppercase tracking-wide">
          {t('price_countdown.system')}
        </span>
      </div>

      {/* Current price — büyük slot */}
      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="text-xs text-apple-gray-mid mb-1">{t('price_countdown.current_price')}</p>
          <SlotNumber
            value={formatPrice(currentPrice)}
            heightEm={1.15}
            className={`text-3xl font-bold leading-none ${triggered ? 'text-apple-red' : 'text-apple-gray-dark'}`}
          />
        </div>
        <div className="text-right">
          <p className="text-xs text-apple-gray-mid">{t('price_countdown.floor_price')}</p>
          <SlotNumber
            value={formatPrice(floorPrice)}
            heightEm={1.15}
            className="text-sm font-semibold text-apple-green leading-none"
          />
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-apple-gray-mid mb-1">
          <span>{t('price_countdown.starting')}: {formatPrice(startPrice)}</span>
          <span>{progress}{t('price_countdown.dropped')}</span>
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-apple-red to-orange-400 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Countdown / expired / floor */}
      {expired ? (
        <div className="bg-gray-50 rounded-xl px-3 py-3 border border-gray-200 flex items-center gap-3">
          <TimerOff className="w-5 h-5 text-gray-400 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-gray-500">{t('price_countdown.expired_short')}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('price_countdown.expired')}</p>
          </div>
        </div>
      ) : !atFloor ? (
        <div className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-gray-100">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-apple-orange" />
            <div>
              <p className="text-xs text-apple-gray-mid">{t('price_countdown.next_drop')}</p>
              {/* Geri sayım — slot animasyonu */}
              <SlotNumber
                value={formatted}
                heightEm={1.2}
                className="text-sm font-bold text-apple-gray-dark font-mono leading-none"
              />
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-apple-gray-mid">{t('price_countdown.drop_amount')}</p>
            <SlotNumber
              value={`-${formatPrice(dropAmount)}`}
              heightEm={1.2}
              className="text-sm font-bold text-apple-red leading-none"
            />
          </div>
        </div>
      ) : (
        <div className="bg-green-50 rounded-xl px-3 py-2.5 border border-green-100 text-center">
          <p className="text-sm font-semibold text-apple-green">{t('price_countdown.floor_reached')}</p>
          <p className="text-xs text-green-600 mt-0.5">{t('price_countdown.floor_no_more')}</p>
        </div>
      )}

      {/* Drop schedule info */}
      <p className="text-xs text-apple-gray-mid text-center mt-2">
        {t('price_countdown.interval_info').replace('{0}', intervalHours).replace('{1}', formatPrice(dropAmount))}
      </p>

      {/* Reserved badge */}
      {isRes && (
        <div className="mt-3 bg-orange-50 border border-orange-100 rounded-xl px-3 py-2 flex items-center gap-2">
          <span className="text-orange-500">🔒</span>
          <p className="text-xs font-medium text-orange-700">{t('price_countdown.reserved_badge')}</p>
        </div>
      )}
    </div>
  );
}
